import { DateTime } from "luxon";
import React, { useMemo, useState } from "react";
import {
  Button, Col, Container, FormControl, FormGroup,
  InputGroup, Row
} from "react-bootstrap";
import { injectIntl } from "react-intl";
import { queryCache, useMutation, useQuery } from "react-query";
import Alert from "react-s-alert";
import AddedEvent from "../../../components/AddedEvent";
import ArchiveHeader from "../../../components/ArchiveHeader";
import CreateItemForm from '../../../components/CreateItemForm';
import PageLoading from "../../../components/Loading";
import SortButton from "../../../components/SortButton";
import Table from '../../../components/TableNew';
import { deleteEvent, getEvents } from './xhr';

const Events = ({
  intl,
  locale,
  setLocale
}) => {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("start");
  const [order, setOrder] = useState("asc");
  const [eventName, setEventName] = useState("");
  const [showSort, setShowSort] = useState(false);

  const { isLoading, isError, error, data: events } = useQuery('events', getEvents, {
    onError: (err) => {
      Alert.error(intl.formatMessage({ id: err.response.data.message }))
    }
  });

  const [deleteEventMutation] = useMutation(deleteEvent, {
    onSuccess: (deletedEvent) => {
      if(queryCache.getQueryData('events')) {
        queryCache.setQueryData('events', events => events
          .filter(event => event._id !== deletedEvent._id)
        )
      }
      Alert.success(intl.formatMessage({ id: 'event.delete-success' }));
    },
    onError: (err) => {
      Alert.error(intl.formatMessage({ id: err.response.data.message }))
    }
  })

  const columns = React.useMemo(
    () => [
      {
        Header: "Events",
        accessor: ({ _id, ...row }) => {
          return Object.values(row).join("|");
        },
        Cell: ({ cell: { value }, row: { original } }) => (
          <AddedEvent
            display={original.displayMode}
            key={original._id}
            id={original._id}
            logo={original.logo}
            name={original.name}
            date={`${intl.formatMessage({id: 'from'})} ${DateTime.fromISO(original.start).setLocale(localStorage.getItem("locale")).toFormat("dd LLL yyyy")} ${intl.formatMessage({id: 'to'})} ${DateTime.fromISO(original.end).setLocale(localStorage.getItem("locale")).toFormat("dd LLL yyyy")}`}
            location={original.venue}
            link={`/manage-event/${original._id}`}
            slug={original.slug}
            deleteFn={(_id) => {
              deleteEventMutation({ _id })
            }}
          />
        ),
      },
    ],
    [intl, deleteEventMutation]
  );

  const sortOptions = useMemo(() => [
    // { label: "trier", value: "trier", sort: (a, b) => true },
    {
      label: "input.event.name",
      value: "name",
      sort: (a, b) => a.name.localeCompare(b.name),
    },
    {
      label: "input.event.dates",
      value: "start",
      sort: (a, b) => DateTime.fromISO(b.start).toMillis() - DateTime.fromISO(a.start).toMillis(),
    },
    {
      label: "input.event.venue", value: "venue",
      sort: (a, b) => (a.venue || `zzzz${a.name}`).localeCompare(b.venue || `zzzz${b.name}`)
    },
    // { label: "input.event.displayMode", value: "displayMode" },
  ], []);

  if (isLoading) {
    return <PageLoading />;
  }

  if (isError) {
    return <span>Error: {error.message}</span>
  }

  return (
    <React.Fragment>
      <ArchiveHeader locale={locale} setLocale={setLocale} intl={intl} title="heading.events" />
      <div className="pad-2rem">
        <Container fluid>
          <Row className="align-items-center m-b-20">
            <Col xl={6}>
              <CreateItemForm
                heading="create.event.form"
                setFunc={setEventName}
                placeholder={intl.formatMessage({ id: "event.form.name" })}
                link={`/event/add/${eventName}`}
              />
            </Col>
            <Col xl={6}>
              <Row className="align-items-center">
                <Col md={6}>
                  <div className="text-right search-container">
                    <FormGroup>
                      <InputGroup onChange={(e) => setSearch(e.target.value)}>
                        <FormControl type="text" />
                        <InputGroup.Append>
                          <Button>
                            <img src="/icons/search.png" alt="plus" />
                          </Button>
                        </InputGroup.Append>
                      </InputGroup>
                    </FormGroup>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="dropdown-filters">
                    {/* <FormGroup controlId="sort" className="sort-filter">
                      <img className="sort-icon" alt="" src="/icons/list.png" />
                      <FormControl
                        onChange={(e) => setSort(e.target.value)}
                        as="select"
                      >
                        {sortOptions.map((o, i) => (
                          <option key={i} value={o.value} selected={o.value === sort}>
                            {intl.formatMessage({ id: o.label })}
                          </option>
                        ))}
                      </FormControl>
                    </FormGroup> */}
                    <SortButton
                      showSort={showSort}
                      setShowSort={setShowSort}
                      sortOptions={sortOptions}
                      intl={intl}
                      setSort={setSort}
                      sort={sort}
                      order={order}
                      setOrder={setOrder}
                    />
                    {/* <Button className="sort-btn" onClick={() => setOrder(order === "asc" ? "desc" : "asc")}>
                    {order === "asc" ? <i className="fas fa-sort-amount-down-alt"></i> : <i className="fas fa-sort-amount-down"></i>}
                    </Button> */}
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            <Col>
              <Table
                columns={columns}
                data={events
                  .sort(sortOptions.find((so) => so.value === sort).sort)
                  .map((d, i) => ({
                    ...d, i: i
                    * (order === "asc" ? 1 : -1)
                  }))
                  .sort((a, b) => a.i - b.i)}
                search={search}
                intl={intl}
              />
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

// const mapStateToProps = (state) => ({
//   events: state.event.allEvents,
//   rooms: state.room.allRooms,
//   speakers: state.speaker.allSpeakers,
//   schedule: state.schedule,
//   userProfile: state.user.profile,
// });

// export default connect(mapStateToProps)(injectIntl(Events));

export default injectIntl(Events);